// src/components/layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { logout } from '../../services/authService';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useAppContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    logout();
    setCurrentUser(null);
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const navLinkClass = (path) =>
    `${isActive(path) ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`;

  const mobileNavLinkClass = (path) =>
    `${isActive(path) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'} block pl-3 pr-4 py-2 border-l-4 text-base font-medium`;

  return (
    <nav className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="text-2xl font-bold flex items-center gradient-text">
                <i className="fas fa-heart mr-2"></i>
                {currentUser?.company_name ? `${currentUser.company_name}` : 'Apprecio'}
              </Link>
            </div>
            <div className="hidden sm:ml-10 sm:flex sm:space-x-8">
              <Link to="/send-appreciation" className={navLinkClass('/send-appreciation')}>
                Send Appreciation
              </Link>
              <Link to="/my-appreciations" className={navLinkClass('/my-appreciations')}>
                My Appreciations
              </Link>
              <Link to="/leaderboard" className={navLinkClass('/leaderboard')}>
                Leaderboard
              </Link>
              <Link to="/my-badges" className={navLinkClass('/my-badges')}>
                Badges
              </Link>
              <Link to="/culture-impact" className={navLinkClass('/culture-impact')}>
                Culture Impact
              </Link>
              <Link to="/values" className={navLinkClass('/values')}>
                Values
              </Link>
              {currentUser && ['admin', 'admin'].includes(currentUser.role) && (
                <Link to="/admin" className={navLinkClass('/admin')}>
                  Admin
                </Link>
              )}
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {!currentUser ? (
              <Link
                to="/login"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Sign In
              </Link>
            ) : (
              <div className="ml-3 relative flex items-center">
                {/* User Search: Future feature */}

                <div className="ml-4 relative" ref={dropdownRef}>
                  <div>
                    <button
                      type="button"
                      className="bg-white rounded-full flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      <span className="sr-only">Open user menu</span>
                      <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        <span className="font-medium text-gray-700">
                          {currentUser.full_name.charAt(0)}
                        </span>
                      </div>
                    </button>
                  </div>

                  {dropdownOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{currentUser.full_name}</p>
                        <p className="text-xs text-gray-500">@{currentUser.username}</p>
                        {currentUser.company_name && (
                          <p className="text-xs text-gray-400 mt-1">{currentUser.company_name}</p>
                        )}
                      </div>
                      <Link
                        to="/my-appreciations"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Appreciations
                      </Link>
                      <Link
                        to="/change-password"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Change Password
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile hamburger button */}
          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <Link to="/send-appreciation" className={mobileNavLinkClass('/send-appreciation')}>
              Send Appreciation
            </Link>
            <Link to="/my-appreciations" className={mobileNavLinkClass('/my-appreciations')}>
              My Appreciations
            </Link>
            <Link to="/leaderboard" className={mobileNavLinkClass('/leaderboard')}>
              Leaderboard
            </Link>
            <Link to="/my-badges" className={mobileNavLinkClass('/my-badges')}>
              Badges
            </Link>
            <Link to="/culture-impact" className={mobileNavLinkClass('/culture-impact')}>
              Culture Impact
            </Link>
            <Link to="/values" className={mobileNavLinkClass('/values')}>
              Values
            </Link>
            {currentUser && ['admin', 'admin'].includes(currentUser.role) && (
              <Link to="/admin" className={mobileNavLinkClass('/admin')}>
                Admin
              </Link>
            )}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            {currentUser ? (
              <>
                <div className="px-4 mb-3">
                  <p className="text-base font-medium text-gray-800">{currentUser.full_name}</p>
                  <p className="text-sm text-gray-500">@{currentUser.username}</p>
                </div>
                <div className="space-y-1">
                  <Link to="/change-password" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Change Password
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                  >
                    Sign out
                  </button>
                </div>
              </>
            ) : (
              <div className="px-4">
                <Link
                  to="/login"
                  className="block text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
